import {JsonObject} from "../shared/json/json-object";
import {FormGen} from "../shared/formgen";
import {JsonProperty} from "../shared/json/json-property";
import {Type} from "../shared/json/helpers";
import {$KTM, $KTS, DefaultObjectLoader, KeyMetadatas, KeyTextStrings, TypedObject} from "../shared/types";
import {UserMetadata, UserProfile} from "./types";
import {getMemberAuth} from "../shared/auth";
import {SvgIcon} from "@mui/material";
import {Facebook, GitHub, Instagram, Twitter, YouTube} from "@mui/icons-material";

@JsonObject()
export abstract class BaseSettings extends TypedObject {

  constructor() {
    super(null, null);
  }

  protected basePath(): string | null {
    return "settings";
  }

  createDefaultLoader(): DefaultObjectLoader<any> {
    return new DefaultObjectLoader<any>(this.basePath(), this.id, this.getType());
  }
}

@JsonObject()
export class GeneralSettings extends BaseSettings {

  readonly id: string = "general";

  @FormGen({name: "Username", type: "string", readonly: true})
  username: string;

  @FormGen({name: "Email", type: "string", readonly: true})
  email: string;

  static async load() {
    const obj = new GeneralSettings();
    await obj.load();
    const memberAuth = getMemberAuth();
    obj.username = memberAuth.member.username;
    obj.email = memberAuth.auth.currentUser.email;
    return obj;
  }

  protected getType(): Type<any> {
    return GeneralSettings;
  }
}

@JsonObject()
export class ProfileSettings extends BaseSettings {

  readonly id: string = "profile";

  constructor(readonly userProfile) {
    super();
  }

  @FormGen({name: "Firstname", type: "string", readonly: true})
  firstname: string;

  @FormGen({name: "Lastname", type: "string", readonly: true})
  lastname: string;

  @FormGen({name: "About", type: "rich_text"})
  about: string;

  @FormGen({name: "Contact enabled", type: "boolean", description: "Allow Showcase users to contact your via a form on your profile."})
  contactEnabled: boolean;

  static async load(userProfile: UserProfile) {
    const obj = new ProfileSettings(userProfile);
    await obj.load();
    const memberAuth = getMemberAuth();
    obj.firstname = memberAuth.member.user.firstname;
    obj.lastname = memberAuth.member.user.lastname;
    obj.about = userProfile.metadata.about;
    obj.contactEnabled = userProfile.metadata.contactEnabled;
    return obj;
  }

  async save(): Promise<void> {
    const metadata: UserMetadata = this.userProfile.metadata;
    metadata.about = this.about;
    metadata.contactEnabled = this.contactEnabled;
    await metadata.save();
    return super.save();
  }

  protected getType(): Type<any> {
    return ProfileSettings;
  }
}

export enum FontSize {
  SMALL = "small",
  DEFAULT = "default",
  LARGE = "large",
}

const FONT_SIZE_KTS = new KeyTextStrings([
  $KTS(FontSize.SMALL, "Small"),
  $KTS(FontSize.DEFAULT, "Default"),
  $KTS(FontSize.LARGE, "Large"),
]);

export enum Timezone {
  AMERICA_LOS_ANGELES = "america_los_angeles",
}

export type TimezoneMetadata = {
  text: string,
  gmt: number,
}

const TIMEZONE_KTM = new KeyMetadatas<TimezoneMetadata>([
  $KTM(Timezone.AMERICA_LOS_ANGELES, {text: "America/Los Angeles", gmt: -8}),
]);

export enum SocialsType {
  X = "x",
  FACEBOOK= "facebook",
  INSTAGRAM= "instagram",
  GITHUB= "github",
  YOUTUBE= "youtube",
}

export type SocialsTypeMetadata = {
  iconType: typeof SvgIcon,
}

export const SOCIALS_TYPE_METADATAS = new KeyMetadatas<SocialsTypeMetadata>([
  $KTM(SocialsType.X, {
    iconType: Twitter,
  }),
  $KTM(SocialsType.FACEBOOK, {
    iconType: Facebook,
  }),
  $KTM(SocialsType.INSTAGRAM, {
    iconType: Instagram,
  }),
  $KTM(SocialsType.GITHUB, {
    iconType: GitHub,
  }),
  $KTM(SocialsType.YOUTUBE, {
    iconType: YouTube,
  }),
]);

@JsonObject()
export class SocialsSettings extends BaseSettings {

  readonly id: string = "socials";

  @FormGen({name: "X", type: "string"})
  @JsonProperty()
  x: string;

  @FormGen({name: "Facebook", type: "string"})
  @JsonProperty()
  facebook: string;

  @FormGen({name: "Instagram", type: "string"})
  @JsonProperty()
  instagram: string;

  @FormGen({name: "GitHub", type: "string"})
  @JsonProperty()
  github: string;

  @FormGen({name: "Youtube", type: "string"})
  @JsonProperty()
  youtube: string;

  static async load() {
    const obj = new SocialsSettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return SocialsSettings;
  }
}

@JsonObject()
export class PreferencesSettings extends BaseSettings {

  readonly id = "preferences";

  @FormGen({
    name: "Order following by", type: "enum", sectionTitle: "Following", enumValues: [
      $KTS("created", "Created date"),
      $KTS("relevance", "Relevance"),
    ],
    enumDefaultKey: "created",
  })
  @JsonProperty()
  orderPosts: string = "created";

  @FormGen({
    sectionTitle: "Display",
    name: "Timezone",
    description: "Select the timezone you'd like to use to display dates and times.",
    type: "enum",
    enumValues: TIMEZONE_KTM.kts().values,
    enumDefaultKey: Timezone.AMERICA_LOS_ANGELES,
  })
  timezone: string = Timezone.AMERICA_LOS_ANGELES;

  @FormGen({
    name: "Font size",
    type: "enum",
    description: "Adjust your font size.",
    enumValues: FONT_SIZE_KTS.values,
    enumDefaultKey: FontSize.DEFAULT,
  })
  @JsonProperty()
  fontSize: string;

  static async load() {
    const obj = new PreferencesSettings();
    await obj.load();
    return obj;
  }

  protected getType(): Type<any> {
    return PreferencesSettings;
  }
}

export class Settings {

  private static instance: Settings;

  static getInstance(): Settings {
    return this.instance;
  }

  static async loadInstance() {
    const member = getMemberAuth().member;
    const userProfile: UserProfile = {
      member: member,
      metadata: await UserMetadata.load(member.memberId),
    };
    this.instance = new Settings(
      userProfile,
      await GeneralSettings.load(),
      await ProfileSettings.load(userProfile),
      await SocialsSettings.load(),
      await PreferencesSettings.load(),
    );
  }

  constructor(
    readonly userProfile: UserProfile,
    readonly general: GeneralSettings,
    readonly profile: ProfileSettings,
    readonly socials: SocialsSettings,
    readonly preferences: PreferencesSettings) {
  }
}